import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { ChevronDown, Mail, TrendingUp, ArrowRight, X, LineChart, ShieldCheck, Cpu, Radar, RefreshCw, FileCheck, IndianRupee } from "lucide-react";
import { PowerBESSLogo } from "../components/PowerBESSLogo";
import { TerrainFloor } from "../components/TerrainFloor";
import { DsmGuard } from "../components/DsmGuard";
import { DigitalTwin } from "../components/DigitalTwin";
import { Reveal, RevealGroup } from "../components/Reveal";
import { ScrollProgress } from "../components/ScrollProgress";
import { NewsletterForm } from "../components/NewsletterForm";
import "../styles/LandingPage.css";
import "../styles/Modules.css";

// ─── Real IEX DAM data from DB ───
const DAM = [4038,4421,4566,4601,4593,4592,4565,4503,4524,4566,4592,4593,4565,4565,4592,4592,4457,4593,4871,5379,5780,6249,5599,5020,5250,6200,6200,6701,8000,8000,7115,5499,4251,3823,3484,3131,3081,3221,3081,3080,2600,2600,2511,2500,2377,2377,2376,2319,2151,2151,2150,2150,2065,2065,2065,2070,2071,2170,2322,2323,2361,2391,2520,2800,2787,2868,2945,3082,3082,3270,3815,4041,5000,7401,10000,10000,10000,10000,9299,6001,6000,6001,6000,6000,4801,5000,4900,4959,4800,5001,5000,4960,4601,4592,4458,4449];

const genDays = (n) => {
  const out = [];
  for (let d = 0; d < n; d++) {
    const v = 0.8 + Math.random() * 0.4;
    out.push(DAM.map((p, i) => {
      let val = p * v + (Math.random() - 0.5) * 400;
      if (i >= 48 && i <= 56 && Math.random() < 0.15) val = 200 + Math.random() * 500;
      if (i >= 72 && i <= 80 && Math.random() < 0.3) val = 9500 + Math.random() * 500;
      return Math.max(50, Math.min(10000, val));
    }));
  }
  return out;
};

// ─── Reveal ───
function R({ children, className = "", id = "", delay = 0 }) {
  const ref = useRef(null);
  const iv = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.section ref={ref} id={id} className={className}
      initial={{ opacity: 0, y: 28 }} animate={iv ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay }}>
      {children}
    </motion.section>
  );
}

// ─── Animated grid background ───

// ─── Scroll hint (fixed at viewport bottom, hides on scroll) ───
function ScrollHint({ onClick }) {
  const [show, setShow] = useState(true);
  useEffect(() => {
    const handler = () => setShow(window.scrollY < 120);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);
  return (
    <motion.button
      className="lp-scroll-hint"
      onClick={onClick}
      initial={{ opacity: 0 }}
      animate={{ opacity: show ? 1 : 0, y: show ? 0 : 8 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      aria-label="Scroll to next section"
      data-testid="hero-scroll-indicator"
      style={{ pointerEvents: show ? 'auto' : 'none' }}>
      <ChevronDown size={14} className="lp-scroll-hint-chev"/>
    </motion.button>
  );
}


function GridBg() {
  return (
    <div className="lp-gridbg">
      {/* Three.js wireframe terrain floor */}
      <TerrainFloor />

      {/* Soft ambient gradient blobs (static, non-reactive) */}
      <div className="lp-bg-blob lp-bg-blob-1" aria-hidden="true" />
      <div className="lp-bg-blob lp-bg-blob-2" aria-hidden="true" />
      <div className="lp-bg-blob lp-bg-blob-3" aria-hidden="true" />

      {/* Radial vignette + noise */}
      <div className="lp-gridbg-radial" />
      <div className="lp-bg-noise" aria-hidden="true" />

      {/* Floating particles */}
      {[...Array(6)].map((_, i) => (
        <motion.div key={i} className="lp-gridbg-particle"
          style={{ left: `${15 + i * 14}%`, top: `${20 + (i % 3) * 25}%` }}
          animate={{ y: [0, -20 - i * 5, 0], opacity: [0.15, 0.4, 0.15] }}
          transition={{ duration: 4 + i, repeat: Infinity, ease: "easeInOut", delay: i * 0.7 }}
        />
      ))}
    </div>
  );
}

// ─── The Lost Day — Fixed vs Smart dispatch comparison on one real IEX day ───
function LostDay() {
  const ref = useRef(null);
  const iv = useInView(ref, { once: true, margin: "-80px" });

  // Strategy windows (4 hr each on 1MW/4MWh)
  const fxC = [8, 23];   // 02:00-05:45  fixed charge
  const fxD = [72, 87];  // 18:00-21:45  fixed discharge
  const smC = [44, 59];  // 11:00-14:45  smart charge (solar trough)
  const smD = [72, 87];  // 18:00-21:45  smart discharge (evening peak)

  // PnL math (each block = 0.25 MWh · 1MW · 15min, RTE 85%)
  const sum = (a, b) => { let s=0; for (let i=a;i<=b;i++) s+=DAM[i]; return s; };
  const fxCost = sum(fxC[0], fxC[1]) * 0.25;
  const dRev   = sum(fxD[0], fxD[1]) * 0.25 * 0.85;
  const smCost = sum(smC[0], smC[1]) * 0.25;
  const fxPnl  = Math.round(dRev - fxCost);
  const smPnl  = Math.round(dRev - smCost);
  const delta  = smPnl - fxPnl;
  const annual = Math.round(delta * 365 / 100000);

  const max = 10000;
  const colorFor = (p) => {
    if (p < 3000) return '#CCFF00';
    if (p < 5000) return '#7BA62D';
    if (p < 7000) return '#5B5B68';
    if (p < 9000) return '#FFB800';
    return '#FF6B35';
  };

  return (
    <motion.div ref={ref} className="ld" data-testid="lost-day"
      initial={{ opacity: 0, y: 24 }} animate={iv ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>

      {/* Header */}
      <div className="ld-hd">
        <div className="ld-hd-left">
          <div className="ld-tag">CASE STUDY · 1 MW / 4 MWh</div>
          <div className="ld-title">Same day. Same battery. <span className="lp-a">Two strategies.</span></div>
          <div className="ld-sub">A real IEX DAM day. The price curve doesn't change. The dispatch does.</div>
        </div>
        <div className="ld-hd-right">
          <div className="ld-delta">
            <span className="ld-delta-n">Rs {delta.toLocaleString('en-IN')}</span>
            <span className="ld-delta-l">missed in a single day</span>
          </div>
        </div>
      </div>

      {/* Strategy comparison strip */}
      <div className="ld-grid">
        {/* Fixed schedule row */}
        <div className="ld-row">
          <div className="ld-row-label">
            <span className="ld-row-name">Fixed schedule</span>
            <span className="ld-row-meta">Charge 02:00 · Discharge 18:00</span>
          </div>
          <div className="ld-bars">
            {DAM.map((p, i) => {
              const isC = i >= fxC[0] && i <= fxC[1];
              const isD = i >= fxD[0] && i <= fxD[1];
              const action = isC ? 'C' : isD ? 'D' : null;
              const h = (p / max) * 100;
              return (
                <div key={i} className="ld-bar-wrap">
                  <div className="ld-bar" style={{ height: `${h}%`, background: action ? (isC ? '#CCFF00' : '#FFB800') : '#2A2A33', opacity: action ? 0.85 : 0.22 }} />
                  {action && <div className="ld-bar-mark" style={{ background: isC ? '#CCFF00' : '#FFB800' }} />}
                </div>
              );
            })}
          </div>
          <div className="ld-row-pnl">
            <span className="ld-pnl-n">+Rs {fxPnl.toLocaleString('en-IN')}</span>
            <span className="ld-pnl-l">PnL · 85% RTE</span>
          </div>
        </div>

        {/* Smart dispatch row */}
        <div className="ld-row ld-row-smart">
          <div className="ld-row-label">
            <span className="ld-row-name"><span className="lp-a">PowerBESS</span> dispatch</span>
            <span className="ld-row-meta">Charge 11:00 (solar trough) · Discharge 18:00</span>
          </div>
          <div className="ld-bars">
            {DAM.map((p, i) => {
              const isC = i >= smC[0] && i <= smC[1];
              const isD = i >= smD[0] && i <= smD[1];
              const action = isC ? 'C' : isD ? 'D' : null;
              const h = (p / max) * 100;
              return (
                <div key={i} className="ld-bar-wrap">
                  <div className="ld-bar" style={{ height: `${h}%`, background: action ? (isC ? '#CCFF00' : '#FFB800') : colorFor(p), opacity: action ? 0.95 : 0.4 }} />
                  {action && <div className="ld-bar-mark" style={{ background: isC ? '#CCFF00' : '#FFB800' }} />}
                </div>
              );
            })}
          </div>
          <div className="ld-row-pnl ld-row-pnl-win">
            <span className="ld-pnl-n lp-a">+Rs {smPnl.toLocaleString('en-IN')}</span>
            <span className="ld-pnl-l">PnL · 85% RTE</span>
          </div>
        </div>

        {/* X-axis */}
        <div className="ld-xaxis">
          <div className="ld-xaxis-label"/>
          <div className="ld-xaxis-ticks">
            <span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>24:00</span>
          </div>
          <div className="ld-xaxis-label"/>
        </div>
      </div>

      {/* Footer takeaway */}
      <div className="ld-foot">
        <div className="ld-foot-stat">
          <span className="ld-foot-n">+{Math.round((delta/fxPnl)*100)}%</span>
          <span className="ld-foot-l">more revenue, same hardware</span>
        </div>
        <div className="ld-foot-divider"/>
        <div className="ld-foot-stat">
          <span className="ld-foot-n">Rs {annual} Lakh</span>
          <span className="ld-foot-l">missed per MW per year, at this gap</span>
        </div>
        <div className="ld-foot-divider"/>
        <div className="ld-foot-stat">
          <span className="ld-foot-n">96</span>
          <span className="ld-foot-l">decisions a fixed schedule cannot make</span>
        </div>
      </div>
    </motion.div>
  );
}

// ─── Smooth bezier path from points ───
function toBezier(prices, toYfn) {
  if (prices.length < 2) return '';
  let d = `M0,${toYfn(prices[0])}`;
  for (let i = 1; i < prices.length; i++) {
    const x0 = ((i-1)/95)*100, x1 = (i/95)*100;
    const y0 = toYfn(prices[i-1]), y1 = toYfn(prices[i]);
    const mx = (x0 + x1) / 2;
    d += ` C${mx},${y0} ${mx},${y1} ${x1},${y1}`;
  }
  return d;
}

// ─── BESS Dispatch Simulator ───
function Sim() {
  const ref = useRef(null);
  const iv = useInView(ref, { margin: "-15% 0px" });
  const [tt, setTt] = useState(0); // cumulative fractional blocks — smooth clock
  const MIN_SOC = 15, MAX_SOC = 95;
  const BLOCK_MS = 110; // one 15-min block ≈ 110ms → full day ≈ 10.5s

  useEffect(() => {
    if (!iv) return;
    let rafId, last = performance.now();
    const tick = (ts) => {
      rafId = requestAnimationFrame(tick);
      const dt = ts - last;
      if (dt < 33) return; // ~30fps — fluid motion, low cost
      last = ts;
      if (document.hidden) return;
      setTt(v => v + Math.min(dt, 200) / BLOCK_MS);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [iv]);

  const t = tt % 96;
  const day = 1 + Math.floor(tt / 96);
  const block = Math.floor(t);
  const frac = t - block;

  // Continuous SoC from the fixed 4h windows: charge 10:00–14:00, discharge 18:00–22:00
  const soc = t < 40 ? MIN_SOC
    : t < 56 ? Math.min(MAX_SOC, MIN_SOC + (t - 40) * 5)
    : t < 72 ? MAX_SOC
    : t < 88 ? Math.max(MIN_SOC, MAX_SOC - (t - 72) * 5)
    : MIN_SOC;

  // Continuous PnL — accrues live through the discharge window
  let pnl = 0;
  if (t > 72) {
    const end = Math.min(t, 88);
    for (let i = 72; i < Math.floor(end); i++) pnl += DAM[i] * 0.25 * 0.85 - 500;
    const f = end - Math.floor(end);
    if (Math.floor(end) < 88 && f > 0) pnl += (DAM[Math.floor(end)] * 0.25 * 0.85 - 500) * f;
    pnl = Math.round(pnl);
  }

  const pr = DAM[block] || 4000;
  const ch = block >= 40 && block < 56 && soc < MAX_SOC;
  const di = block >= 72 && block < 88 && soc > MIN_SOC;
  const mode = ch ? 'CHARGE' : di ? 'DIS' : 'STANDBY';
  const mc = ch ? '#CCFF00' : di ? '#FFB800' : '#6B6B7B';
  const hr = Math.floor(block / 4);
  const mn = (block % 4) * 15;
  const time = `${String(hr).padStart(2,'0')}:${String(mn).padStart(2,'0')}`;
  const toY = (p) => 88 - (p / 10000) * 76;
  const socColor = soc > 70 ? '#CCFF00' : soc > 35 ? '#FFB800' : '#ef4444';

  // Smooth traced path — exact partial bezier tip (De Casteljau split), so the
  // pointer glides along the very curve the final trace will follow
  const fullPath = toBezier(DAM, toY);
  const lerp = (a, c, u) => a + (c - a) * u;
  let tracedPath = toBezier(DAM.slice(0, block + 1), toY);
  let cx = (block / 95) * 100, cy = toY(pr);
  if (frac > 0 && block < 95) {
    const x0 = (block / 95) * 100, x1 = ((block + 1) / 95) * 100, mx = (x0 + x1) / 2;
    const y0 = toY(DAM[block]), y1 = toY(DAM[block + 1]);
    const u = frac;
    const ax = lerp(x0, mx, u), ay = y0;                       // A = lerp(P0, C1)
    const bx = mx, by = lerp(y0, y1, u);                       // B = lerp(C1, C2)
    const gx = lerp(mx, x1, u), gy = y1;                       // G = lerp(C2, P3)
    const dx2 = lerp(ax, bx, u), dy2 = lerp(ay, by, u);        // D = lerp(A, B)
    const ex = lerp(bx, gx, u), ey = lerp(by, gy, u);          // E = lerp(B, G)
    const px = lerp(dx2, ex, u), py = lerp(dy2, ey, u);        // P = point on curve
    tracedPath += ` C${ax.toFixed(2)},${ay.toFixed(2)} ${dx2.toFixed(2)},${dy2.toFixed(2)} ${px.toFixed(2)},${py.toFixed(2)}`;
    cx = px; cy = py;
  }
  const areaPath = (block > 0 || frac > 0) ? tracedPath + ` L${cx},92 L0,92 Z` : '';

  return (
    <div ref={ref} className="sim" data-testid="bess-model-animation">
      {/* ── Header ── */}
      <div className="sim-hd">
        <div className="sim-hd-left">
          <div className="sim-dot" style={{background:mc,boxShadow:`0 0 8px ${mc}`}}/>
          <span className="sim-hd-title">Live Dispatch Simulation</span>
          <span className="sim-hd-day">Day {day}</span>
        </div>
        <div className="sim-hd-right">
          <span className="sim-hd-time" style={{color:mc}}>{time}</span>
          <span className="sim-hd-badge" style={{color:mc,borderColor:`${mc}40`}}>{mode}</span>
        </div>
      </div>

      {/* ── Chart ── */}
      <div className="sim-chart-wrap">
        <div className="sim-svg-box">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="sim-svg">
          <defs>
            <linearGradient id="sA" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={mc} stopOpacity="0.45"/>
              <stop offset="25%" stopColor={mc} stopOpacity="0.2"/>
              <stop offset="60%" stopColor={mc} stopOpacity="0.06"/>
              <stop offset="100%" stopColor={mc} stopOpacity="0"/>
            </linearGradient>
            <linearGradient id="sL" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={mc} stopOpacity="0.12"/>
              <stop offset="50%" stopColor={mc} stopOpacity="0.6"/>
              <stop offset="85%" stopColor={mc} stopOpacity="1"/>
              <stop offset="100%" stopColor={mc} stopOpacity="1"/>
            </linearGradient>
            <radialGradient id="sD">
              <stop offset="0%" stopColor={mc} stopOpacity="1"/>
              <stop offset="20%" stopColor={mc} stopOpacity="0.5"/>
              <stop offset="50%" stopColor={mc} stopOpacity="0.1"/>
              <stop offset="100%" stopColor={mc} stopOpacity="0"/>
            </radialGradient>
            <filter id="sG" x="-100%" y="-100%" width="300%" height="300%"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
          </defs>

          {/* 4-hour charge / discharge windows */}
          <rect x={(40/96)*100} y="2" width={(16/96)*100} height="90" fill="rgba(204,255,0,0.03)" rx="0.5"/>
          <rect x={(72/96)*100} y="2" width={(16/96)*100} height="90" fill="rgba(255,184,0,0.03)" rx="0.5"/>
          <line x1={(40/96)*100} y1="2" x2={(40/96)*100} y2="92" stroke="#CCFF00" strokeWidth="0.04" opacity="0.12"/>
          <line x1={(56/96)*100} y1="2" x2={(56/96)*100} y2="92" stroke="#CCFF00" strokeWidth="0.04" opacity="0.12"/>
          <line x1={(72/96)*100} y1="2" x2={(72/96)*100} y2="92" stroke="#FFB800" strokeWidth="0.04" opacity="0.12"/>
          <line x1={(88/96)*100} y1="2" x2={(88/96)*100} y2="92" stroke="#FFB800" strokeWidth="0.04" opacity="0.12"/>

          {/* Grid */}
          {[2000,4000,6000,8000,10000].map((v,i)=>{
            const y=toY(v);
            return <g key={i}>
              <line x1="3.5" y1={y} x2="100" y2={y} stroke="rgba(255,255,255,0.03)" strokeWidth="0.08"/>
              <text x="0.2" y={y+0.4} fill="#52525B" fontSize="1.15" fontFamily="monospace">{v>=10000?'10k':`${v/1000}k`}</text>
            </g>;
          })}

          {/* Thresholds */}
          <line x1="3.5" y1={toY(3000)} x2="100" y2={toY(3000)} stroke="#CCFF00" strokeWidth="0.1" strokeDasharray="0.5,1" opacity="0.4"/>
          <line x1="3.5" y1={toY(6000)} x2="100" y2={toY(6000)} stroke="#FFB800" strokeWidth="0.1" strokeDasharray="0.5,1" opacity="0.4"/>
          <text x="98" y={toY(3000)+1.6} fill="#CCFF00" fontSize="1.2" fontFamily="monospace" textAnchor="end" opacity="0.6">BUY</text>
          <text x="98" y={toY(6000)-0.5} fill="#FFB800" fontSize="1.2" fontFamily="monospace" textAnchor="end" opacity="0.6">SELL</text>

          {/* Future path — dashed ghost */}
          <path d={fullPath} fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="0.15" strokeDasharray="0.3,0.8"/>

          {/* Traced area — bright fill */}
          {block > 0 && <path d={areaPath} fill="url(#sA)"/>}

          {/* Traced line — thin, precise, accurate */}
          {block > 0 && <path d={tracedPath} fill="none" stroke="url(#sL)" strokeWidth="0.3" strokeLinecap="round" strokeLinejoin="round"/>}

          {/* Scanline */}
          <line x1={cx} y1="2" x2={cx} y2="92" stroke={mc} strokeWidth="0.04" opacity="0.08"/>
          {/* Horizontal reference */}
          <line x1={cx} y1={cy} x2="100" y2={cy} stroke={mc} strokeWidth="0.03" strokeDasharray="0.3,0.6" opacity="0.15"/>
        </svg>
        {/* 4-hour window labels */}
        <span className="sim-zone-label" style={{ left: `${(48/96)*100}%`, color: '#CCFF00' }}>CHARGE &middot; 4H</span>
        <span className="sim-zone-label" style={{ left: `${(80/96)*100}%`, color: '#FFB800' }}>DISCHARGE &middot; 4H</span>
        {/* PowerBESS logo pointer — HTML overlay (always square, screen-size independent) */}
        <img
          src="/logo.svg"
          alt=""
          aria-hidden="true"
          className="sim-pointer-logo"
          style={{ left: `${cx}%`, top: `${cy}%` }}
          data-testid="sim-pointer-logo"
        />
        </div>
        <div className="sim-time-axis"><span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>24:00</span></div>
      </div>

      {/* ── Stats: Battery (large) + 3 info cells ── */}
      <div className="sim-stats">
        {/* Battery — landscape orientation */}
        <div className="sim-batt-cell">
          <div className="sim-batt-row">
            <div className="sim-batt-shell" style={{borderColor: ch ? 'rgba(204,255,0,0.25)' : di ? 'rgba(255,184,0,0.25)' : 'rgba(255,255,255,0.04)'}}>
              <div className="sim-batt-body">
                {[...Array(10)].map((_,i)=>{
                  const th=(i+1)*10;
                  const filled=soc>=th;
                  const col=th>80?'#CCFF00':th>50?'rgba(204,255,0,0.65)':th>20?'#FFB800':'#ef4444';
                  return <div key={i} className="sim-batt-seg" style={{opacity:filled?1:0.04,background:filled?col:'#1A1A22'}}/>;
                })}
              </div>
              <div className="sim-batt-tip"/>
            </div>
            <div className="sim-batt-meta">
              <div className="sim-batt-pct" style={{color:socColor}}>{Math.round(soc)}<span>%</span></div>
              <div className="sim-batt-label">1 MW / 4 MWh &middot; 4h window &middot; 85% RTE</div>
            </div>
          </div>
        </div>

        {/* Info cells */}
        <div className="sim-info-grid">
          <div className="sim-info">
            <span className="sim-info-label">Price / MWh</span>
            <span className="sim-info-val" style={{color: pr>6000?'#FFB800':pr<3000?'#CCFF00':'#fff'}}>Rs {pr.toLocaleString('en-IN')}</span>
            <span className="sim-info-sub">Block {block+1}/96</span>
          </div>
          <div className="sim-info">
            <span className="sim-info-label">AI Decision</span>
            <span className="sim-info-val" style={{color:mc}}>{ch ? 'BUY' : di ? 'SELL' : 'HOLD'}</span>
            <span className="sim-info-sub">{ch?'4h charge window':di?'4h discharge window':'Outside window — hold'}</span>
          </div>
          <div className="sim-info">
            <span className="sim-info-label">Today's PnL</span>
            <span className="sim-info-val" style={{color:pnl>0?'#CCFF00':'#6B6B7B'}}>+Rs {pnl.toLocaleString('en-IN')}</span>
            <span className="sim-info-sub">Revenue &middot; 85% RTE</span>
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <div className="sim-ft">
        <span>Spread <strong className="lp-a">Rs 7,935</strong></span>
        <span>Oracle <strong className="lp-a">94.0%</strong></span>
        <span>Daily <strong className="lp-gold">~Rs 25,360</strong></span>
        <span className="sim-ft-brand"><span className="sim-dot" style={{background:mc,boxShadow:`0 0 5px ${mc}`,width:4,height:4}}/>PowerBESS.ai</span>
      </div>
    </div>
  );
}

// ─── Heatmap (canvas — 2,880 cells in one paint, keeps scroll buttery) ───
function Heatmap() {
  const cvRef = useRef(null);
  useEffect(() => {
    const cv = cvRef.current;
    if (!cv) return;
    const d = genDays(30);
    const gc = (v) => {
      const r = v / 10000;
      if (r < 0.25) return `rgba(0,150,255,${0.15+r*1.5})`;
      if (r < 0.45) return `rgba(204,255,0,${0.1+r*0.7})`;
      if (r < 0.65) return `rgba(255,220,0,${0.25+r*0.5})`;
      if (r < 0.85) return `rgba(255,140,0,${0.4+r*0.4})`;
      return `rgba(255,60,20,${0.6+r*0.4})`;
    };
    const draw = () => {
      const w = cv.clientWidth, h = cv.clientHeight;
      if (!w || !h) return;
      const dpr = window.devicePixelRatio || 1;
      cv.width = w * dpr; cv.height = h * dpr;
      const ctx = cv.getContext('2d');
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, w, h);
      const cw = w / 96, chh = h / 30;
      for (let i = 0; i < 30; i++) {
        for (let j = 0; j < 96; j++) {
          ctx.fillStyle = gc(d[i][j]);
          ctx.fillRect(j * cw + 0.25, i * chh + 0.25, cw - 0.5, chh - 0.5);
        }
      }
    };
    draw();
    window.addEventListener('resize', draw);
    return () => window.removeEventListener('resize', draw);
  }, []);
  return (
    <div className="lp-heatmap" data-testid="price-heatmap">
      <canvas ref={cvRef} className="lp-hm-canvas" />
      <div className="lp-hm-lbls"><span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>24:00</span></div>
    </div>
  );
}

// ─── FAQ Accordion Item ───
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item ${open ? 'faq-open' : ''}`} data-testid="faq-item">
      <button className="faq-q" onClick={() => setOpen(o => !o)}>
        <span>{q}</span>
        <span className={`faq-arrow ${open ? 'faq-arrow-open' : ''}`} aria-hidden="true">
          <PowerBESSLogo size={26} />
        </span>
      </button>
      <div className={`faq-a ${open ? 'faq-a-open' : ''}`}>
        <p>{a}</p>
      </div>
    </div>
  );
}



// ═══════════════════════════
// PAGE
// ═══════════════════════════
export default function LandingPage() {
  const nav = useNavigate();
  const [showNav, setShowNav] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const fn = () => setShowNav(window.scrollY > window.innerHeight * 0.4);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  const scrollTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const headerH = 70;
    const y = el.getBoundingClientRect().top + window.pageYOffset - headerH;
    window.scrollTo({ top: y, behavior: "smooth" });
  }, []);

  return (
    <div className="lp-root" data-testid="landing-page">
      <Helmet>
        <html lang="en-IN" />
        <title>PowerBESS.ai — Intelligence layer for grid-scale batteries.</title>
        <meta name="description" content="The operating system for India's grid-scale battery storage. AI-driven IEX dispatch (DAM/RTM/GDAM), automated CERC DSM 2024 compliance, and a live battery digital twin — built for IPPs, traders and renewable developers." />
        <link rel="canonical" href="https://powerbess.ai/" />
        <meta property="og:title" content="PowerBESS.ai · The Operating System for India's Energy Storage" />
        <meta property="og:description" content="One platform, three jobs: forecast-driven IEX dispatch · CERC DSM 2024 compliance · live battery digital twin. Built for IPPs, traders and BESS operators in India." />
        <meta property="og:url" content="https://powerbess.ai/" />
        <meta name="twitter:title" content="PowerBESS.ai · BESS arbitrage + DSM compliance + digital twin" />
        <meta name="twitter:description" content="India's grid-scale battery storage OS. AI dispatch on IEX, automated CERC DSM compliance, live digital twin. 94% Oracle capture." />
      </Helmet>

      <ScrollProgress />

      {/* ─── Sticky Nav ─── */}
      <AnimatePresence>
        {showNav && (
          <motion.nav className="lp-nav" initial={{ y: -60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -60, opacity: 0 }} transition={{ duration: 0.2 }} data-testid="sticky-nav">
            <div className="lp-nav-in">
              <div className="lp-nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                <PowerBESSLogo size={24} /><span>Power<span className="lp-a">BESS</span>.ai</span>
              </div>
              <div className="lp-nav-links">
                <button onClick={() => scrollTo('platform')}>Platform</button>
                <button onClick={() => scrollTo('compliance')}>Compliance</button>
                <button onClick={() => scrollTo('twin')}>Digital Twin</button>
                <button onClick={() => nav('/forecast-accuracy')} data-testid="nav-accuracy-sticky">Forecast</button>
              </div>
              <div className="lp-nav-right">
                <button className="lp-nbtn" onClick={() => nav('/backtest-request')}>Request Backtest</button>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>

      {/* ═══ HERO ═══ */}
      <section className="lp-hero" data-testid="hero-section">
        <GridBg />
        <header className="lp-hdr-v2">
          <div className="lp-hdr-v2-in">
            <div className="lp-hdr-v2-logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} data-testid="header-logo">
              <PowerBESSLogo size={32} />
              <span>Power<span className="lp-a">BESS</span>.ai</span>
            </div>
            <nav className="lp-hdr-v2-nav">
              <button onClick={() => scrollTo('platform')}>Platform</button>
              <button onClick={() => scrollTo('compliance')}>Compliance</button>
              <button onClick={() => scrollTo('twin')}>Digital Twin</button>
              <button onClick={() => nav('/forecast-accuracy')} data-testid="nav-accuracy-header">Forecast</button>
            </nav>
            <div className="lp-hdr-v2-right">
              <button className="lp-hdr-v2-cta" onClick={() => nav('/backtest-request')} data-testid="hero-cta-header">
                <span className="lp-hdr-v2-cta-text-long">Request Backtest</span>
                <span className="lp-hdr-v2-cta-text-short">Backtest</span>
                <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </header>

        <div className="lp-hero-v2" data-testid="hero-v2">
          <motion.div className="lp-hero-v2-eyebrow"
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.4 }}
            data-testid="hero-badge">
            <span className="lp-hero-v2-eb-text">
              Building <strong>OS</strong> for
              <svg className="lp-hero-v2-eb-flag" viewBox="0 0 30 20" aria-label="India" role="img">
                <rect width="30" height="20" rx="2" fill="#FFF"/>
                <rect width="30" height="6.67" y="0" fill="#FF9933"/>
                <rect width="30" height="6.67" y="13.33" fill="#138808"/>
                <circle cx="15" cy="10" r="2.4" fill="none" stroke="#000080" strokeWidth="0.45"/>
                <circle cx="15" cy="10" r="0.6" fill="#000080"/>
                {[...Array(24)].map((_, i) => {
                  const angle = (i * 15 * Math.PI) / 180;
                  return (
                    <line key={i}
                      x1={15} y1={10}
                      x2={15 + Math.cos(angle) * 2.4}
                      y2={10 + Math.sin(angle) * 2.4}
                      stroke="#000080" strokeWidth="0.18"/>
                  );
                })}
              </svg>
              <span className="lp-hero-v2-eb-poss">'s</span>
              <strong>34.7 GWh</strong> Storage Pipeline
            </span>
          </motion.div>

          <motion.h1 className="lp-hero-v2-h1"
            initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }}
            data-testid="hero-title">
            The intelligence layer<br/>for <span className="lp-a">grid-scale batteries.</span>
          </motion.h1>

          <motion.p className="lp-hero-v2-sub"
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.5 }}>
            PowerBESS runs a <strong>live digital twin</strong> of your battery — forecasting IEX prices, dispatching every 15 minutes, keeping every block inside the <strong>DSM band</strong>, and pricing degradation into each trade. One platform. Every decision your battery makes.
          </motion.p>

          <motion.div className="lp-hero-v2-ctas"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.4 }}>
            <button className="lp-hero-v2-cta-primary" onClick={() => nav('/backtest-request')} data-testid="hero-cta-primary">
              Request a backtest <ArrowRight size={16}/>
            </button>
            <button className="lp-hero-v2-cta-ghost" onClick={() => scrollTo('platform')} data-testid="hero-cta-secondary">
              See how it works
            </button>
          </motion.div>
        </div>

        {/* Scroll indicator — fixed at viewport bottom, auto-hides on scroll */}
        <ScrollHint onClick={() => scrollTo('problem')} />
      </section>

      {/* ─── Scrolling Marquee of BESS Pipeline Operators (between hero and problem) ─── */}
      <motion.section className="lp-marquee-section"
        initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        data-testid="hero-marquee">
        <div className="lp-hero-v2-marquee-label">India's BESS pipeline — companies building the next <strong>236 GWh</strong></div>
        <div className="lp-hero-v2-marquee">
          <div className="lp-hero-v2-marquee-track">
            {(() => {
              const partners = [
                { name: 'Tata Power', src: '/partners/tata-power.svg' },
                { name: 'ACME Solar', src: '/partners/acme.png' },
                { name: 'ReNew', src: '/partners/renew.svg' },
                { name: 'Adani Power', src: '/partners/adani.svg' },
                { name: 'JSW Energy', src: '/partners/jsw.svg' },
                { name: 'NTPC', src: '/partners/ntpc.svg' },
                { name: 'Greenko', src: '/partners/greenko.svg' },
                { name: 'Serentica', src: '/partners/serentica.svg' },
                { name: 'Azure Power', src: '/partners/azure.svg' },
                { name: 'Vikram Solar', src: '/partners/vikram.svg' },
                { name: 'Vena Energy', src: '/partners/vena.svg' },
                { name: 'Sembcorp', src: '/partners/sembcorp.svg' },
              ];
              return [...partners, ...partners].map((p, i) => (
                <div key={i} className="lp-hero-v2-marquee-item" title={p.name}>
                  <img src={p.src} alt={p.name} loading="eager" decoding="async" draggable="false" />
                </div>
              ));
            })()}
          </div>
        </div>
      </motion.section>

      {/* ═══ THE PROBLEM ═══ */}
      <R className="lp-sec" id="problem">
        <div className="lp-w">
          <div className="lp-tag">THE PROBLEM</div>
          <h2 className="lp-h2" data-testid="crisis-title">The market moves 96 times a day.<br /><span className="lp-a">Your battery schedule was set last quarter.</span></h2>
          <p className="lp-p">Fixed dispatch was designed for a flat grid. India's grid is anything but flat — DAM prices range from <strong>Rs 2,065 at midday</strong> to <strong>Rs 10,000 by evening</strong>, settled every 15 minutes across three exchanges. And the spread is only the first problem: every deviation from your filed schedule settles under <strong>DSM</strong>, and every wasted cycle burns warranty life.</p>
          <LostDay />
        </div>
      </R>

      {/* ═══ THE OPPORTUNITY ═══ */}
      <R className="lp-sec lp-sec-a" id="opportunity">
        <div className="lp-w">
          <div className="lp-tag">THE OPPORTUNITY</div>
          <h2 className="lp-h2" data-testid="insight-title">Volatility is the product. <span className="lp-a">Not the problem.</span></h2>
          <p className="lp-p">30 days of IEX prices. 96 blocks per day. The daily rhythm — solar trough, evening spike — is structured opportunity for intelligent dispatch.</p>
          <div className="lp-ins-row">
            <div className="lp-ins"><span className="lp-ins-n lp-a">Rs 2,065</span><span className="lp-ins-l">Midday trough</span></div>
            <div className="lp-ins"><span className="lp-ins-n lp-gold">Rs 10,000</span><span className="lp-ins-l">Evening cap-hit (12% of blocks)</span></div>
            <div className="lp-ins"><span className="lp-ins-n">Rs 7,935</span><span className="lp-ins-l">Daily arbitrage spread</span></div>
          </div>
          <Heatmap />
        </div>
      </R>

      {/* ═══ THE PLATFORM — One OS, three jobs ═══ */}
      <R className="lp-sec" id="platform">
        <div className="lp-w">
          <div className="lp-tag">THE PLATFORM</div>
          <h2 className="lp-h2" data-testid="platform-title">One battery. Three full-time jobs.<br /><span className="lp-a">One operating system.</span></h2>
          <p className="lp-p">A grid-scale battery is a trading desk, a regulated grid entity, and a degrading electrochemical machine — all at the same time. PowerBESS runs all three jobs from a single live model of your asset.</p>
          <div className="lp-os-grid" data-testid="platform-grid">
            <button className="lp-os-card" onClick={() => scrollTo('system')} data-testid="platform-card-dispatch">
              <span className="lp-os-num">01</span>
              <span className="lp-os-ic"><LineChart size={17} /></span>
              <span className="lp-os-t">Forecast &amp; Dispatch</span>
              <span className="lp-os-d">Price forecasts across DAM, RTM and GDAM become charge / discharge decisions every 15 minutes — net of cycle cost, SoC limits and PPA commitments.</span>
              <span className="lp-os-link">See the module <ArrowRight size={11} /></span>
            </button>
            <button className="lp-os-card" onClick={() => scrollTo('compliance')} data-testid="platform-card-compliance">
              <span className="lp-os-num">02</span>
              <span className="lp-os-ic"><ShieldCheck size={17} /></span>
              <span className="lp-os-t">Compliance Manager</span>
              <span className="lp-os-d">Schedules, revisions and block-wise deviation tracking under CERC DSM Regulations 2024 — handled before penalties exist, with an audit-ready trail.</span>
              <span className="lp-os-link">See the module <ArrowRight size={11} /></span>
            </button>
            <button className="lp-os-card" onClick={() => scrollTo('twin')} data-testid="platform-card-twin">
              <span className="lp-os-num">03</span>
              <span className="lp-os-ic"><Cpu size={17} /></span>
              <span className="lp-os-t">Digital Twin</span>
              <span className="lp-os-d">A live virtual replica of your BESS — SoH, cycle count, RTE drift, thermal envelopes. Every decision is checked against the physics of your asset.</span>
              <span className="lp-os-link">See the module <ArrowRight size={11} /></span>
            </button>
          </div>
        </div>
      </R>

      {/* ═══ MODULE 01 — FORECAST & DISPATCH ═══ */}
      <R className="lp-sec lp-sec-a" id="system">
        <div className="lp-w">
          <div className="lp-tag">MODULE 01 · FORECAST &amp; DISPATCH</div>
          <h2 className="lp-h2" data-testid="system-title">The system reads the market.<br />The battery <span className="lp-a">follows the signal.</span></h2>
          <p className="lp-p lp-pn">PowerBESS analyzes years of IEX market history across DAM, RTM and GDAM — fused with live grid load, weather, generation mix and dozens more signal streams. The engine identifies the regime, calibrates confidence, and places your battery's <strong>4-hour charge window</strong> in the solar trough and its <strong>4-hour discharge window</strong> across the evening peak. Every 15 minutes, net of cycle cost.</p>
          <Sim />
        </div>
      </R>

      {/* ═══ MODULE 02 — COMPLIANCE MANAGER ═══ */}
      <R className="lp-sec" id="compliance">
        <div className="lp-w">
          <div className="lp-tag">MODULE 02 · COMPLIANCE MANAGER</div>
          <h2 className="lp-h2" data-testid="compliance-title">96 blocks a day.<br /><span className="lp-a">Zero of them out of band.</span></h2>
          <p className="lp-p lp-pn">Under CERC's <strong>DSM Regulations 2024</strong>, every 15-minute deviation from your filed schedule settles at market-linked rates. PowerBESS forecasts deviations before they happen, files revisions in time, and keeps the audit trail — compliance as a property of the system, not a monthly fire-drill.</p>
          <DsmGuard />
          <div className="lp-mod-feats" data-testid="compliance-features">
            <div className="lp-mod-feat">
              <div className="lp-mod-feat-t"><Radar size={15} />Deviation forecasting</div>
              <p className="lp-mod-feat-d">Error bands predicted per 15-minute block — the system knows a breach is coming before the block settles.</p>
            </div>
            <div className="lp-mod-feat">
              <div className="lp-mod-feat-t"><RefreshCw size={15} />Auto schedule revisions</div>
              <p className="lp-mod-feat-d">Intraday revision windows used surgically — re-declare before the deviation, not after the penalty.</p>
            </div>
            <div className="lp-mod-feat">
              <div className="lp-mod-feat-t"><FileCheck size={15} />Audit-ready reporting</div>
              <p className="lp-mod-feat-d">Block-wise deviation logs and DSM account statements in regulator format. Export in one click.</p>
            </div>
          </div>
        </div>
      </R>

      {/* ═══ MODULE 03 — DIGITAL TWIN ═══ */}
      <R className="lp-sec lp-sec-a" id="twin">
        <div className="lp-w">
          <div className="lp-tag">MODULE 03 · DIGITAL TWIN</div>
          <h2 className="lp-h2" data-testid="twin-title">A living model of your battery.<br /><span className="lp-a">Down to the cycle.</span></h2>
          <p className="lp-p lp-pn">The moment you onboard, we spin up a <strong>live software replica</strong> of your BESS — cells, thermals, cycle history — kept in sync every 15 minutes. Every dispatch is validated against the twin before it touches the hardware.</p>
          <DigitalTwin />
          <div className="lp-mod-feats" data-testid="twin-features">
            <div className="lp-mod-feat">
              <div className="lp-mod-feat-t"><IndianRupee size={15} />Degradation-aware dispatch</div>
              <p className="lp-mod-feat-d">Every trade is net of cycle cost. If the spread doesn't beat the rupees your cells lose, the system holds.</p>
            </div>
            <div className="lp-mod-feat">
              <div className="lp-mod-feat-t"><ShieldCheck size={15} />Warranty guard-rails</div>
              <p className="lp-mod-feat-d">DoD, C-rate and temperature envelopes enforced at dispatch level. The OEM warranty stays intact while revenue runs.</p>
            </div>
            <div className="lp-mod-feat">
              <div className="lp-mod-feat-t"><TrendingUp size={15} />Augmentation forecasting</div>
              <p className="lp-mod-feat-d">SoH projections show when capacity needs topping up — budget augmentation years ahead, not after a surprise.</p>
            </div>
          </div>
        </div>
      </R>

      {/* ═══ SCALE ═══ */}
      <R className="lp-sec lp-sec-a" id="scale">
        <div className="lp-w">
          <div className="lp-tag">THE SCALE</div>
          <h2 className="lp-h2" data-testid="scale-title"><span className="lp-a">236 GWh</span> by 2032. Every GWh needs an operating system.</h2>
          <div className="lp-tl" data-testid="growth-timeline">
            {[{ y: "2025", v: "0.8 GWh", s: "Operational", a: true }, { y: "2026", v: "6 GWh", s: "Commissioning" }, { y: "2030", v: "50+ GWh", s: "Projected" }, { y: "2032", v: "236 GWh", s: "CEA Target" }].map((t, i) => (
              <div key={i} className={`lp-tl-i ${t.a ? 'lp-tl-ac' : ''}`}>
                <div className="lp-tl-d" /><div className="lp-tl-y">{t.y}</div><div className="lp-tl-v">{t.v}</div><div className="lp-tl-s">{t.s}</div>
              </div>
            ))}
            <div className="lp-tl-ln" />
          </div>
        </div>
      </R>

      {/* ═══ FAQ ═══ */}
      <R className="lp-sec" id="faq">
        <div className="lp-w">
          <div className="lp-tag">FAQ</div>
          <h2 className="lp-h2" data-testid="faq-title">Frequently asked <span className="lp-a">questions.</span></h2>
          <RevealGroup className="faq-list" stagger={0.06} data-testid="faq-list">
            {[
              { q: "What is PowerBESS.ai?", a: "PowerBESS.ai is the operating system for India's grid-scale battery storage. One platform runs three jobs: forecast-driven dispatch (when to charge and discharge, every 15 minutes), compliance management under CERC's DSM framework, and a live digital twin that tracks the health of your asset down to the cycle. On blind test data, our dispatch engine captures 94% of theoretically perfect (Oracle) arbitrage profit." },
              { q: "What is BESS arbitrage?", a: "Battery Energy Storage System (BESS) arbitrage means buying electricity when prices are low (typically during midday solar surplus) and selling it back to the grid when prices are high (typically during evening peak demand). On India's IEX, this spread can exceed Rs 7,000/MWh in a single day." },
              { q: "How does PowerBESS.ai decide when to charge and discharge?", a: "Our system analyzes years of historical IEX market data across DAM, RTM and GDAM — fused with live grid load, weather, generation mix and dozens more signal streams. It identifies the current market regime (scarcity, surplus, festival, or normal), calibrates its forecast confidence, and generates a charge/discharge/hold decision for every 15-minute time block — 96 decisions per day, each one net of cycle cost and SoC limits." },
              { q: "Does PowerBESS manage DSM compliance?", a: "Yes. Under CERC's Deviation Settlement Mechanism (DSM) Regulations 2024, deviations from your filed schedule are settled every 15-minute block at market-linked rates. PowerBESS forecasts deviation risk per block, files schedule revisions inside the allowed windows, and maintains block-wise audit logs — so your DSM account stays clean without a back-office scramble." },
              { q: "What exactly is the digital twin?", a: "A live virtual replica of your battery system. It continuously tracks state of charge, state of health, equivalent full cycles, round-trip-efficiency drift, and thermal behaviour against OEM warranty envelopes. Every dispatch decision is validated against the twin before it reaches the battery." },
              { q: "Won't aggressive trading wear out my battery faster?", a: "No — that's exactly what the twin prevents. Degradation is priced in rupees per MWh-cycle and added to every trade decision. If a spread doesn't beat the cycle cost, the system holds. DoD, C-rate and temperature guard-rails are enforced at dispatch level, so revenue never comes at the cost of your warranty." },
              { q: "What does '94% Oracle capture' mean?", a: "The Oracle is a hypothetical trader with perfect knowledge of all future prices. No real system can match it. We measure how close we get: on 2,496 held-out test windows (data the system never saw during training), PowerBESS captured 94% of the profit that a perfect-foresight trader would have earned." },
              { q: "Can I try PowerBESS on my site's data?", a: "Yes. Share your site location, battery capacity, and current dispatch method — we'll run a backtest on actual IEX historical data and show you the revenue gap between your current approach and what's possible. No commitment required." },
            ].map((faq, i) => (
              <FaqItem key={i} q={faq.q} a={faq.a} />
            ))}
          </RevealGroup>
        </div>
      </R>

      {/* ═══ CTA ═══ */}
      <R className="lp-sec lp-cta-sec" id="contact">
        <div className="lp-w lp-cta-c">
          <h2 className="lp-h2" data-testid="cta-title">See what your battery is <span className="lp-a">leaving on the table.</span></h2>
          <p className="lp-p lp-pn">Share your site specs. We'll backtest on actual IEX data and show the revenue gap.</p>
          <div className="lp-cta-btns">
            <button className="lp-btn" onClick={() => nav('/backtest-request')} data-testid="cta-contact-btn"><Mail size={15} /> Request a Backtest</button>
          </div>
          <p className="lp-cta-note">No commitment. No sales pitch. Just data.</p>
        </div>
      </R>

      {/* Footer */}
      <footer className="lp-ft" data-testid="footer">
        {/* Newsletter band */}
        <div className="lp-ft-newsletter">
          <div className="lp-ft-newsletter-in">
            <div className="lp-ft-newsletter-left">
              <div className="lp-ft-newsletter-eyebrow">SIGNAL · WEEKLY</div>
              <div className="lp-ft-newsletter-title">India's BESS market, decoded weekly.</div>
              <div className="lp-ft-newsletter-sub">Tender awards, IEX spreads, regulatory shifts, and what they mean for storage operators. <strong>Built by analysts who read CERC drafts so you don't have to.</strong></div>
            </div>
            <NewsletterForm />
          </div>
        </div>

        <div className="lp-ft-top">
          {/* Newsroom / Updates strip */}
          <div className="lp-ft-news" data-testid="footer-newsroom">
            <div className="lp-ft-news-head">
              <div className="lp-ft-news-eyebrow">NEWSROOM</div>
              <a href="#" className="lp-ft-news-all">All updates →</a>
            </div>
            <RevealGroup className="lp-ft-news-list" stagger={0.08}>
              {[
                { tag: 'MoU', date: 'Feb 2026', title: 'PowerBESS signs MoU with a Tier-1 IPP for 100 MWh BESS optimization pilot.' },
                { tag: 'Research', date: 'Jan 2026', title: 'White paper: 94% Oracle PnL capture across 2,496 IEX held-out windows.' },
                { tag: 'Press', date: 'Dec 2025', title: 'PowerBESS featured in IESA panel on India\'s 236 GWh storage roadmap.' },
              ].map((n, i) => (
                <a key={i} href="#" className="lp-ft-news-item" data-testid={`footer-news-${i}`}>
                  <div className="lp-ft-news-meta">
                    <span className="lp-ft-news-tag">{n.tag}</span>
                    <span className="lp-ft-news-date">{n.date}</span>
                  </div>
                  <div className="lp-ft-news-title">{n.title}</div>
                  <span className="lp-ft-news-arrow"><ArrowRight size={13}/></span>
                </a>
              ))}
            </RevealGroup>
          </div>
          <div className="lp-ft-cols">
            {/* Col 1: Brand + description */}
            <div className="lp-ft-col lp-ft-col-brand">
              <div className="lp-ft-brand"><PowerBESSLogo size={32} /><span>Power<span className="lp-a">BESS</span>.ai</span></div>
              <p className="lp-ft-desc">The operating system for grid-scale batteries — forecast-driven 15-minute dispatch, DSM compliance management, and a live digital twin of your asset. One platform for every decision your BESS makes.</p>
              <div className="lp-ft-socials">
                <a href="#" className="lp-ft-social" data-testid="footer-linkedin" aria-label="LinkedIn">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </a>
                <a href="#" className="lp-ft-social" data-testid="footer-twitter" aria-label="X (Twitter)">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </a>
              </div>
            </div>

            {/* Col 2: Product */}
            <div className="lp-ft-col">
              <div className="lp-ft-col-title">Product</div>
              <button onClick={() => scrollTo('system')} className="lp-ft-link">Forecast &amp; Dispatch</button>
              <button onClick={() => scrollTo('compliance')} className="lp-ft-link">Compliance Manager</button>
              <button onClick={() => scrollTo('twin')} className="lp-ft-link">Digital Twin</button>
              <button onClick={() => nav('/forecast-accuracy')} className="lp-ft-link" data-testid="footer-accuracy">Forecast Report</button>
              <button onClick={() => nav('/backtest-request')} className="lp-ft-link">Request a Backtest</button>
            </div>

            {/* Col 3: Company */}
            <div className="lp-ft-col">
              <div className="lp-ft-col-title">Company</div>
              <span className="lp-ft-text">B-506/7, Bhutani Alphathum</span>
              <span className="lp-ft-text">Sector 90, Noida</span>
              <span className="lp-ft-text">Uttar Pradesh, India</span>
              <a href="mailto:contact@powerbess.ai" className="lp-ft-email">contact@powerbess.ai</a>
            </div>

            {/* Col 4: Legal & More */}
            <div className="lp-ft-col">
              <div className="lp-ft-col-title">Legal</div>
              <a href="/terms" className="lp-ft-link" data-testid="footer-terms">Terms &amp; Conditions</a>
              <a href="/privacy" className="lp-ft-link" data-testid="footer-privacy">Privacy Policy</a>
              <a href="#" className="lp-ft-link" data-testid="footer-research">Research Paper</a>
              <a href="#" className="lp-ft-link" data-testid="footer-team">Our Team</a>
            </div>
          </div>
        </div>
        <div className="lp-ft-bottom">
          <span>&copy; 2026 PowerBESS.ai. All rights reserved.</span>
        </div>
        <div className="lp-ft-watermark" aria-hidden="true" data-testid="footer-watermark">
          <svg viewBox="0 0 1200 142" role="presentation" focusable="false">
            <defs>
              <linearGradient id="wmGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#9A9AA8" stopOpacity="0.55" />
                <stop offset="55%" stopColor="#6E6E7A" stopOpacity="0.45" />
                <stop offset="100%" stopColor="#2A2A33" stopOpacity="0.35" />
              </linearGradient>
            </defs>
            <text x="600" y="135" textAnchor="middle" textLength="1180" lengthAdjust="spacingAndGlyphs"
              fontFamily="'Clash Display','Outfit','Space Grotesk',sans-serif" fontWeight="700" fontSize="155" letterSpacing="-2"
              fill="url(#wmGrad)">POWERBESS AI</text>
          </svg>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div className="lp-mo" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowModal(false)} data-testid="contact-modal">
            <motion.div className="lp-mo-box" initial={{ opacity: 0, scale: 0.95, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 16 }} onClick={e => e.stopPropagation()}>
              <button className="lp-mo-x" onClick={() => setShowModal(false)}><X size={15} /></button>
              <div className="lp-mo-ic"><Mail size={22} /></div>
              <h3 className="lp-mo-t">Request a Backtest</h3>
              <p className="lp-mo-d">Share your battery specs — we'll run a performance analysis on real IEX data.</p>
              <a href="mailto:contact@powerbess.ai?subject=Backtest Request&body=Site:%0ABattery (MW/MWh):%0ADispatch:%0AName:" className="lp-btn lp-mo-btn" data-testid="modal-email-btn"><Mail size={14} /> contact@powerbess.ai</a>
              <p className="lp-mo-note">We respond within 24 hours</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
